import { useState, useEffect } from 'react';
import { Box, CircularProgress, Typography, Paper } from '@mui/material';
import { AccessTime } from '@mui/icons-material';
import theme from 'theme';
import { getCurrentEduBudgetPeriod } from './getEduBudgetPeriod';
import { DarkTooltip } from '../../../components/DarkTooltip';
import { useAuth } from '../../../auth';
import { useTenant } from '../../../hooks/useTenant';

const EduBudgetTile = ({ preliminaryEduBudget, userAvailableEduBudget }) => {
  const { user } = useAuth();
  const [currentEduBudgetPeriod, setCurrentEduBudgetPeriod] = useState('');
  const tenant = useTenant();

  const showPreliminaryEduBudget = preliminaryEduBudget > 0;
  const EDU_BUDGET_AMOUNT = tenant?.educationBudget.amount || 0;
  const TOTAL_EDU_BUDGET =
    userAvailableEduBudget > EDU_BUDGET_AMOUNT ? userAvailableEduBudget : EDU_BUDGET_AMOUNT;

  useEffect(() => {
    setCurrentEduBudgetPeriod(getCurrentEduBudgetPeriod());
  }, [setCurrentEduBudgetPeriod]);

  const roundedEduBudget =
    Math.round(Math.min(userAvailableEduBudget, TOTAL_EDU_BUDGET) * 100) / 100;
  const roundedPreliminaryEduBudget = Math.round(preliminaryEduBudget * 100) / 100;

  return (
    <Paper
      sx={{
        padding: '1.915rem 1.5rem 0.875rem 1.5rem',
        borderRadius: '0.25rem',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
      }}
      elevation={1}
    >
      <Box
        component="div"
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: '1.1rem',
          paddingBottom: '1.175rem',
          borderBottom: `1px solid ${theme.palette.divider}`,
        }}
      >
        <Typography
          sx={{ color: '#000000', fontSize: '1.125rem', fontWeight: 500 }}
          variant="body1"
          component="div"
        >
          Education Budget
        </Typography>
      </Box>

      <Box
        component="div"
        sx={{ justifyContent: 'space-between', display: 'flex', flexDirection: 'row' }}
      >
        <Box
          component="div"
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            padding: '0',
            gap: '1.5rem',
            flex: 'none',
            order: '0',
            flexGrow: '0',
          }}
        >
          <Box
            component="div"
            sx={{ alignItems: 'flex-start', display: 'flex', flexDirection: 'column' }}
          >
            {showPreliminaryEduBudget && (
              <DarkTooltip title="Preliminary amount, there are not completed requests">
                <Typography
                  sx={{ color: '#000000', display: 'flex', fontWeight: 300, cursor: 'pointer' }}
                  variant="body2"
                  component="div"
                >
                  <AccessTime
                    sx={{
                      color: '#686868',
                      marginRight: '0.5rem',
                      width: '1.2rem',
                      height: '1.2rem',
                    }}
                  />
                  {roundedPreliminaryEduBudget} EUR
                </Typography>
              </DarkTooltip>
            )}
            <Typography
              sx={{
                marginTop: '0.313rem',
                marginBottom: '0.313rem',
                fontWeight: 500,
                fontStyle: 'normal',
                color: '#000000',
              }}
              variant="body1"
            >
              <span>
                {user.availableEduBudget <= TOTAL_EDU_BUDGET
                  ? Math.round((TOTAL_EDU_BUDGET - user.availableEduBudget) * 100) / 100
                  : Math.round(TOTAL_EDU_BUDGET * 100) / 100}{' '}
                EUR used
              </span>
            </Typography>

            <Typography
              sx={{ fontStyle: 'normal', fontWeight: 'normal', color: '#4c4c4c' }}
              variant="body2"
              component="div"
            >
              {currentEduBudgetPeriod}
            </Typography>
          </Box>
        </Box>
        <Box sx={{ position: 'relative', display: 'inline-flex' }}>
          <CircularProgress
            size={75}
            thickness={0.5}
            sx={{
              ...(userAvailableEduBudget === 0
                ? { color: 'transparent' }
                : { color: theme.palette.primary.main }),
            }}
            variant="determinate"
            value={(() => {
              if (userAvailableEduBudget === TOTAL_EDU_BUDGET) {
                return 100;
              }

              return 100 - ((TOTAL_EDU_BUDGET + userAvailableEduBudget) * 100) / TOTAL_EDU_BUDGET;
            })()}
            aria-hidden="true"
          />
          <DarkTooltip
            title={
              userAvailableEduBudget === 0
                ? 'No budget left for the current period'
                : 'Total amount of budget left'
            }
          >
            <Box
              sx={{
                top: 7,
                left: 7,
                bottom: 7,
                right: 7,
                position: 'absolute',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: '50%',
                backgroundColor: '#F7F7F9',
                cursor: 'pointer',
              }}
            >
              <Typography
                sx={{
                  fontWeight: 500,
                  color: userAvailableEduBudget === 0 ? '#CCCCCC' : undefined,
                }}
                variant="subtitle1"
                id="availableEduBudget"
                aria-label={`Education budget left: ${roundedEduBudget} EUR`}
              >
                {roundedEduBudget}
              </Typography>
            </Box>
          </DarkTooltip>
        </Box>
      </Box>
    </Paper>
  );
};

export default EduBudgetTile;
